import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    FaPlus, FaSearch, FaFilter, FaShoppingCart, FaUsers, FaChartLine,
} from 'react-icons/fa';
import Pagination, { PaginationMeta } from './Pagination';
import styles from './styles/Proveedores.module.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

type TabKey = 'ordenes' | 'proveedores';

interface Proveedor {
    id: number;
    nombre: string;
    cuit: string;
    rubro: string;
    contacto: string;
    telefono: string;
    email: string;
    direccion: string;
    condicion_pago: string;
    activo: boolean;
    ordenes: number;
    pendientes: number;
    total_comprado: number;
    ultima_compra: string | null;
}

interface OrdenItem {
    id: number;
    detalle: string;
    cantidad: number;
    precio_unitario: number;
    /** Articulo del catalogo al que se imputa el stock (null = sin vincular). */
    id_producto: number | null;
}

interface OrdenCompra {
    id: number;
    numero: string;
    proveedor: string;
    id_proveedor: number;
    fecha: string;
    estado: 'Pendiente' | 'Parcial' | 'Recibida';
    total: number;
    resumen: string;
    recibida_en: string | null;
    items: OrdenItem[];
}

interface Kpis {
    pendientes: number;
    proveedores_activos: number;
    total_mes: number;
}

const money = (n: number) => `$${Math.round(n || 0).toLocaleString('es-AR')}`;

const compactMoney = (n: number) => {
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `$${Math.round(n / 1_000)}k`;
    return `$${Math.round(n)}`;
};

const ESTADOS: ('Todos' | OrdenCompra['estado'])[] = ['Todos', 'Pendiente', 'Parcial', 'Recibida'];

const Proveedores: React.FC = () => {
    const [tab, setTab] = useState<TabKey>('ordenes');

    const [ordenes, setOrdenes] = useState<OrdenCompra[]>([]);
    const [proveedores, setProveedores] = useState<Proveedor[]>([]);
    const [kpis, setKpis] = useState<Kpis>({ pendientes: 0, proveedores_activos: 0, total_mes: 0 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [search, setSearch] = useState('');
    const [debounced, setDebounced] = useState('');
    const [estado, setEstado] = useState<'Todos' | OrdenCompra['estado']>('Todos');
    const [proveedorFiltro, setProveedorFiltro] = useState('Todos');
    const [detalle, setDetalle] = useState<OrdenCompra | null>(null);
    /** Orden que se esta recibiendo (pasa a Recibida y da entrada al stock). */
    const [recepcion, setRecepcion] = useState<OrdenCompra | null>(null);

    // Paginacion en memoria: el listado se filtra en el backend pero se pagina
    // aca, que alcanza para el volumen de ordenes de una PyME.
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);

    useEffect(() => {
        const id = window.setTimeout(() => setDebounced(search), 300);
        return () => window.clearTimeout(id);
    }, [search]);

    const cargarProveedores = useCallback(async () => {
        try {
            const res = await fetch(`${API}/admin/proveedores`);
            const data = await res.json();
            setProveedores(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error cargando proveedores', err);
        }
    }, []);

    const cargarOrdenes = useCallback(async () => {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams();
        if (debounced.trim()) params.set('q', debounced.trim());
        if (estado !== 'Todos') params.set('estado', estado);
        if (proveedorFiltro !== 'Todos') params.set('proveedor', proveedorFiltro);
        try {
            const res = await fetch(`${API}/admin/ordenes-compra?${params.toString()}`);
            const data = await res.json();
            setOrdenes(Array.isArray(data.ordenes) ? data.ordenes : []);
            if (data.kpis) setKpis(data.kpis);
        } catch (err) {
            console.error('Error cargando ordenes', err);
            setError('No se pudieron cargar las órdenes de compra.');
        } finally {
            setLoading(false);
        }
    }, [debounced, estado, proveedorFiltro]);

    useEffect(() => { cargarProveedores(); }, [cargarProveedores]);
    useEffect(() => { cargarOrdenes(); }, [cargarOrdenes]);
    useEffect(() => { setPage(1); }, [debounced, estado, proveedorFiltro, perPage, tab]);

    const filas = tab === 'ordenes' ? ordenes : proveedores;
    const totalPages = Math.max(1, Math.ceil(filas.length / perPage));
    const pageSafe = Math.min(page, totalPages);
    const meta: PaginationMeta = {
        page: pageSafe,
        per_page: perPage,
        total: filas.length,
        total_pages: totalPages,
        has_next: pageSafe < totalPages,
        has_prev: pageSafe > 1,
    };

    const ordenesPagina = useMemo(
        () => ordenes.slice((pageSafe - 1) * perPage, pageSafe * perPage),
        [ordenes, pageSafe, perPage],
    );

    const proveedoresFiltrados = useMemo(() => {
        const q = debounced.trim().toLowerCase();
        const base = q
            ? proveedores.filter(p =>
                `${p.nombre} ${p.rubro} ${p.cuit} ${p.contacto}`.toLowerCase().includes(q))
            : proveedores;
        return base.slice((pageSafe - 1) * perPage, pageSafe * perPage);
    }, [proveedores, debounced, pageSafe, perPage]);

    return (
        <div className={styles.prov}>
            <header className={styles.head}>
                <div>
                    <h2 className={styles.headTitle}>Compras y Proveedores</h2>
                    <p className={styles.headSub}>
                        Gestión de abastecimiento, órdenes de compra y proveedores.
                    </p>
                </div>
                <button type="button" className={styles.primaryBtn}>
                    <FaPlus /> Nueva Orden de Compra
                </button>
            </header>

            <div className={styles.tabs} role="tablist">
                {([['ordenes', 'Órdenes de Compra'], ['proveedores', 'Proveedores']] as [TabKey, string][])
                    .map(([key, label]) => (
                        <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={tab === key}
                            className={`${styles.tab} ${tab === key ? styles.tabActive : ''}`}
                            onClick={() => setTab(key)}
                        >
                            {label}
                        </button>
                    ))}
            </div>

            <section className={styles.kpis}>
                <article className={styles.kpi}>
                    <div className={styles.kpiHead}>
                        <span className={styles.kpiLabel}>Órdenes pendientes</span>
                        <FaShoppingCart className={styles.kpiIcon} />
                    </div>
                    <strong className={styles.kpiValue}>{kpis.pendientes} Órdenes</strong>
                    <span className={styles.kpiHint}>Sin recepción completa</span>
                </article>
                <article className={styles.kpi}>
                    <div className={styles.kpiHead}>
                        <span className={styles.kpiLabel}>Proveedores activos</span>
                        <FaUsers className={styles.kpiIcon} />
                    </div>
                    <strong className={styles.kpiValue}>{kpis.proveedores_activos} Registrados</strong>
                    <span className={styles.kpiHint}>Con alta vigente</span>
                </article>
                <article className={styles.kpi}>
                    <div className={styles.kpiHead}>
                        <span className={styles.kpiLabel}>Total compras mes</span>
                        <FaChartLine className={styles.kpiIcon} />
                    </div>
                    <strong className={styles.kpiValue}>{compactMoney(kpis.total_mes)}</strong>
                    <span className={styles.kpiHint}>Mes en curso</span>
                </article>
            </section>

            {error && <div className={styles.errorBar}>{error}</div>}

            <section className={styles.panel}>
                <div className={styles.filters}>
                    <div className={styles.searchBox}>
                        <FaSearch className={styles.searchIcon} />
                        <input
                            type="search"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder={tab === 'ordenes'
                                ? 'Buscar por OC o proveedor…'
                                : 'Buscar por nombre, rubro o CUIT…'}
                            aria-label="Buscar"
                        />
                    </div>

                    {tab === 'ordenes' && (
                        <>
                            <span className={styles.filtersLabel}><FaFilter /> Filtrar por:</span>
                            <select
                                value={proveedorFiltro}
                                onChange={e => setProveedorFiltro(e.target.value)}
                                aria-label="Proveedor"
                            >
                                <option value="Todos">Proveedor: Todos</option>
                                {proveedores.map(p => (
                                    <option key={p.id} value={p.id}>{p.nombre}</option>
                                ))}
                            </select>
                            <select
                                value={estado}
                                onChange={e => setEstado(e.target.value as typeof estado)}
                                aria-label="Estado"
                            >
                                {ESTADOS.map(s => (
                                    <option key={s} value={s}>
                                        {s === 'Todos' ? 'Estado: Todos' : s}
                                    </option>
                                ))}
                            </select>
                        </>
                    )}
                </div>

                <div className={styles.tableWrap}>
                    {tab === 'ordenes' ? (
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Nro OC</th>
                                    <th>Fecha</th>
                                    <th>Proveedor</th>
                                    <th>Producto / Detalle</th>
                                    <th className={styles.num}>Total</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr><td colSpan={7} className={styles.empty}>Cargando órdenes…</td></tr>
                                )}
                                {!loading && ordenesPagina.length === 0 && (
                                    <tr><td colSpan={7} className={styles.empty}>
                                        No hay órdenes para los filtros seleccionados.
                                    </td></tr>
                                )}
                                {!loading && ordenesPagina.map(o => (
                                    <tr key={o.id}>
                                        <td className={styles.mono}>#{o.numero}</td>
                                        <td className={styles.nowrap}>{o.fecha}</td>
                                        <td>{o.proveedor}</td>
                                        <td className={styles.ellipsis} title={o.resumen}>{o.resumen || '—'}</td>
                                        <td className={`${styles.num} ${styles.strong}`}>{money(o.total)}</td>
                                        <td>
                                            <span className={`${styles.badge} ${styles[`badge${o.estado}`]}`}>
                                                {o.estado}
                                            </span>
                                        </td>
                                        <td>
                                            <div className={styles.rowActions}>
                                                <button
                                                    type="button"
                                                    className={styles.linkBtn}
                                                    onClick={() => setDetalle(o)}
                                                >
                                                    Detalle
                                                </button>
                                                {o.estado !== 'Recibida' && (
                                                    <button
                                                        type="button"
                                                        className={styles.linkBtn}
                                                        onClick={() => setRecepcion(o)}
                                                    >
                                                        Recibir
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Proveedor</th>
                                    <th>CUIT</th>
                                    <th>Rubro</th>
                                    <th>Contacto</th>
                                    <th>Condición</th>
                                    <th className={styles.num}>Órdenes</th>
                                    <th className={styles.num}>Total comprado</th>
                                    <th>Última compra</th>
                                </tr>
                            </thead>
                            <tbody>
                                {proveedoresFiltrados.length === 0 && (
                                    <tr><td colSpan={8} className={styles.empty}>
                                        No hay proveedores cargados.
                                    </td></tr>
                                )}
                                {proveedoresFiltrados.map(p => (
                                    <tr key={p.id}>
                                        <td className={styles.strong}>{p.nombre}</td>
                                        <td className={styles.mono}>{p.cuit || '—'}</td>
                                        <td>{p.rubro || '—'}</td>
                                        <td>
                                            <div className={styles.contacto}>
                                                <span>{p.contacto || '—'}</span>
                                                <small>{p.telefono || p.email || ''}</small>
                                            </div>
                                        </td>
                                        <td className={styles.muted}>{p.condicion_pago || '—'}</td>
                                        <td className={styles.num}>{p.ordenes}</td>
                                        <td className={`${styles.num} ${styles.strong}`}>
                                            {money(p.total_comprado)}
                                        </td>
                                        <td className={styles.nowrap}>{p.ultima_compra || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <Pagination
                    meta={meta}
                    onPageChange={setPage}
                    onPerPageChange={setPerPage}
                    itemLabel={tab === 'ordenes' ? 'órdenes' : 'proveedores'}
                />
            </section>

            {detalle && (
                <div
                    className={styles.overlay}
                    role="presentation"
                    onClick={e => { if (e.target === e.currentTarget) setDetalle(null); }}
                >
                    <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Detalle de orden">
                        <div className={styles.modalHead}>
                            <div>
                                <h3>Orden #{detalle.numero}</h3>
                                <p>{detalle.proveedor} · {detalle.fecha}</p>
                            </div>
                            <button
                                type="button"
                                className={styles.modalClose}
                                onClick={() => setDetalle(null)}
                                aria-label="Cerrar"
                            >×</button>
                        </div>

                        <div className={styles.modalBody}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Detalle</th>
                                        <th className={styles.num}>Cantidad</th>
                                        <th className={styles.num}>Precio unit.</th>
                                        <th className={styles.num}>Subtotal</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detalle.items.map((i, idx) => (
                                        <tr key={idx}>
                                            <td>{i.detalle}</td>
                                            <td className={styles.num}>{i.cantidad}</td>
                                            <td className={styles.num}>{money(i.precio_unitario)}</td>
                                            <td className={`${styles.num} ${styles.strong}`}>
                                                {money(i.cantidad * i.precio_unitario)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className={styles.modalFoot}>
                            <span className={`${styles.badge} ${styles[`badge${detalle.estado}`]}`}>
                                {detalle.estado}
                            </span>
                            <strong className={styles.modalTotal}>{money(detalle.total)}</strong>
                        </div>
                    </div>
                </div>
            )}

            {recepcion && (
                <RecepcionModal
                    orden={recepcion}
                    onClose={() => setRecepcion(null)}
                    onRecibida={() => {
                        setRecepcion(null);
                        // La orden cambia de estado y los KPIs de pendientes con
                        // ella: se recarga el listado completo.
                        cargarOrdenes();
                    }}
                />
            )}
        </div>
    );
};

/* ------------------------------------------------------- recepcion de una OC */

interface Articulo {
    idProductos: number;
    producto: string;
    sku: string;
}

interface RecepcionModalProps {
    orden: OrdenCompra;
    onClose: () => void;
    onRecibida: () => void;
}

/**
 * Recepcion de una orden de compra.
 *
 * Pasa la orden a "Recibida" y da entrada al stock de lo comprado. El detalle
 * de la OC es texto libre del proveedor, asi que cada linea se vincula a un
 * articulo del catalogo antes de confirmar: las lineas sin vincular se reciben
 * igual pero no mueven stock, y el modal lo dice antes de confirmar.
 */
const RecepcionModal: React.FC<RecepcionModalProps> = ({ orden, onClose, onRecibida }) => {
    const [articulos, setArticulos] = useState<Articulo[]>([]);
    const [locales, setLocales] = useState<{ locales_id: number; nombre_local: string }[]>([]);
    const [idLocal, setIdLocal] = useState<number | ''>('');
    // Vinculo elegido por linea: {id_item: id_producto}.
    const [vinculos, setVinculos] = useState<Record<number, string>>(() =>
        Object.fromEntries(orden.items.map(i => [i.id, i.id_producto ? String(i.id_producto) : ''])),
    );
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetch(`${API}/admin/stock/articulos`)
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data?.articulos)) setArticulos(data.articulos);
                if (Array.isArray(data?.locales)) {
                    setLocales(data.locales);
                    // Con un solo local la eleccion es unica: se resuelve sola.
                    if (data.locales.length >= 1) setIdLocal(data.locales[0].locales_id);
                }
            })
            .catch(() => setError('No se pudo cargar el catálogo de artículos.'));
    }, []);

    const sinVincular = orden.items.filter(i => !vinculos[i.id]).length;

    const confirmar = async () => {
        if (!idLocal) {
            setError('Elegí la sucursal que recibe la mercadería.');
            return;
        }
        setGuardando(true);
        setError(null);
        try {
            const res = await fetch(`${API}/admin/ordenes-compra/${orden.id}/recibir`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id_local: idLocal, vinculos }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.error || 'No se pudo recibir la orden.');
                return;
            }
            onRecibida();
        } catch {
            setError('Error de conexión.');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <div
            className={styles.overlay}
            role="presentation"
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Recibir orden">
                <div className={styles.modalHead}>
                    <div>
                        <h3>Recibir orden #{orden.numero}</h3>
                        <p>{orden.proveedor} · {orden.fecha}</p>
                    </div>
                    <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Cerrar">
                        ×
                    </button>
                </div>

                <div className={styles.modalBody}>
                    {locales.length > 1 && (
                        <label className={styles.field}>
                            <span>Sucursal que recibe</span>
                            <select
                                value={idLocal}
                                onChange={e => setIdLocal(Number(e.target.value))}
                            >
                                <option value="">Seleccionar…</option>
                                {locales.map(l => (
                                    <option key={l.locales_id} value={l.locales_id}>
                                        {l.nombre_local}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}

                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Detalle de la orden</th>
                                <th className={styles.num}>Cant.</th>
                                <th>Artículo del catálogo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orden.items.map(item => (
                                <tr key={item.id}>
                                    <td>{item.detalle}</td>
                                    <td className={styles.num}>{item.cantidad}</td>
                                    <td>
                                        <select
                                            value={vinculos[item.id] || ''}
                                            onChange={e => setVinculos(prev => ({
                                                ...prev,
                                                [item.id]: e.target.value,
                                            }))}
                                            aria-label={`Artículo para ${item.detalle}`}
                                        >
                                            <option value="">Sin vincular (no suma stock)</option>
                                            {articulos.map(a => (
                                                <option key={a.idProductos} value={a.idProductos}>
                                                    {a.producto}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {sinVincular > 0 && (
                        <p className={styles.warnMsg}>
                            {sinVincular === 1
                                ? '1 línea quedó sin vincular y no va a sumar stock.'
                                : `${sinVincular} líneas quedaron sin vincular y no van a sumar stock.`}
                        </p>
                    )}
                    {error && <p className={styles.errorMsg}>{error}</p>}
                </div>

                <div className={styles.modalFoot}>
                    <button type="button" className={styles.ghostBtn} onClick={onClose}>
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className={styles.primaryBtn}
                        onClick={confirmar}
                        disabled={guardando}
                    >
                        {guardando ? 'Recibiendo…' : 'Confirmar recepción'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Proveedores;
